import React, { useEffect, useState } from 'react';
import { Baby, TrendingUp, Euro, Inbox, Users, FileText, UserPlus, Target, CheckCircle2, Building2, Loader } from 'lucide-react';
import { analyticsApi } from '../../utils/api';
import { formatMontant } from '../../utils/calculs';

const pct = (n) => `${n || 0}%`;

function Kpi({ icon: Icon, label, value, sub, tone = 'navy' }) {
  const tones = {
    navy: 'bg-navy-50 text-navy-700', emerald: 'bg-emerald-50 text-emerald-700',
    gold: 'bg-gold-100 text-gold-800', cyan: 'bg-cyan-50 text-cyan-700', violet: 'bg-violet-50 text-violet-700',
  };
  return (
    <div className="apple-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon size={16} /></span>
        <span className="text-[12.5px] text-navy-500">{label}</span>
      </div>
      <div className="text-[26px] font-bold text-navy-900 leading-none">{value}</div>
      {sub && <div className="text-[12px] text-navy-400 mt-1">{sub}</div>}
    </div>
  );
}

// Barres horizontales simples (répartition)
function Bars({ data, labelKey, valueKey, max, color = '#1e2d4a', suffix = '' }) {
  const m = max || Math.max(1, ...data.map(d => d[valueKey] || 0));
  if (!data.length) return <div className="text-[13px] text-navy-300">Aucune donnée.</div>;
  return (
    <div className="space-y-2">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-40 text-[12.5px] text-navy-600 truncate">{d[labelKey] || '—'}</div>
          <div className="flex-1 bg-cream-200 rounded-full h-4 overflow-hidden">
            <div className="h-4 rounded-full" style={{ width: `${Math.round(((d[valueKey] || 0) / m) * 100)}%`, background: d.couleur || color }} />
          </div>
          <div className="w-16 text-right text-[12.5px] font-semibold text-navy-800">{d[valueKey] || 0}{suffix}</div>
        </div>
      ))}
    </div>
  );
}

const Section = ({ icon: Icon, title, children, right }) => (
  <div className="apple-card p-5">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-serif text-[17px] text-navy-900 flex items-center gap-2"><Icon size={17} className="text-navy-500" /> {title}</h3>
      {right}
    </div>
    {children}
  </div>
);

export default function StatistiquesPage() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    analyticsApi.getStatistiques().then(r => setD(r.data)).catch(e => setErr(e.response?.data?.error || 'Erreur de chargement.')).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 text-center text-navy-300 flex items-center justify-center gap-2"><Loader size={18} className="animate-spin" /> Chargement des statistiques…</div>;
  if (err || !d) return <div className="py-16 text-center text-rose-500">{err || 'Indisponible.'}</div>;

  return (
    <div className="space-y-5 max-w-5xl">
      <div>
        <h1 className="font-serif text-[30px] text-navy-900">Statistiques & Pilotage</h1>
        <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-1" />
        <p className="text-navy-400 text-[14px]">Vue d'ensemble de l'activité pour piloter l'entreprise.</p>
      </div>

      {/* KPI principaux */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Baby} label="Enfants accueillis" value={d.enfants.total_actifs} sub={`${d.enfants.en_attente} en attente · ${d.enfants.capacite_totale} places`} tone="emerald" />
        <Kpi icon={Target} label="Taux d'occupation" value={pct(d.enfants.taux_occupation)} sub={`sur ${d.enfants.capacite_totale} places d'agrément`} tone="navy" />
        <Kpi icon={Euro} label="Revenus mensuels" value={formatMontant(d.finances.mrr)} sub={`${d.finances.nb_contrats_actifs} contrat(s) actif(s)`} tone="gold" />
        <Kpi icon={TrendingUp} label="Taux de transformation" value={pct(d.demandes.taux_transformation)} sub={`${d.demandes.converties}/${d.demandes.total} demandes converties`} tone="violet" />
      </div>

      {/* Demandes entrantes */}
      <Section icon={Inbox} title="Demandes de préinscription"
        right={<span className="text-[12.5px] text-navy-400">{d.demandes.total} au total</span>}>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <div className="text-[12px] uppercase tracking-wide text-navy-400 font-semibold mb-2">Par structure</div>
            <Bars data={d.demandes.par_structure} labelKey="structure" valueKey="n" />
          </div>
          <div>
            <div className="text-[12px] uppercase tracking-wide text-navy-400 font-semibold mb-2">Entonnoir de conversion</div>
            <div className="space-y-2">
              {[
                { l: 'Demandes reçues', v: d.demandes.total, c: '#64748b' },
                { l: 'Demandes traitées (réponse)', v: d.demandes.repondues, c: '#0ea5e9' },
                { l: 'Converties en inscription', v: d.demandes.converties, c: '#16a34a' },
              ].map((x, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-44 text-[12.5px] text-navy-600">{x.l}</div>
                  <div className="flex-1 bg-cream-200 rounded-full h-4 overflow-hidden">
                    <div className="h-4 rounded-full" style={{ width: `${d.demandes.total ? Math.round((x.v / d.demandes.total) * 100) : 0}%`, background: x.c }} />
                  </div>
                  <div className="w-10 text-right text-[12.5px] font-semibold text-navy-800">{x.v}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-[12.5px]">
              <span className="text-cyan-700">Taux de réponse : <strong>{pct(d.demandes.taux_reponse)}</strong></span>
              <span className="text-emerald-700">Taux de transformation : <strong>{pct(d.demandes.taux_transformation)}</strong></span>
            </div>
          </div>
        </div>
      </Section>

      {/* Occupation par structure */}
      <Section icon={Building2} title="Occupation par structure">
        <div className="space-y-3">
          {d.enfants.par_structure.map((s, i) => (
            <div key={i}>
              <div className="flex items-center justify-between text-[13px] mb-1">
                <span className="font-medium text-navy-800 flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.couleur || '#1e2d4a' }} />{s.structure}</span>
                <span className="text-navy-500">{s.actifs}/{s.capacite} · <strong>{pct(s.taux_occupation)}</strong>{s.en_attente ? ` · ${s.en_attente} en attente` : ''}</span>
              </div>
              <div className="bg-cream-200 rounded-full h-3 overflow-hidden">
                <div className="h-3 rounded-full" style={{ width: `${Math.min(100, s.taux_occupation)}%`, background: s.taux_occupation >= 100 ? '#dc2626' : (s.couleur || '#1e2d4a') }} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Devis */}
        <Section icon={FileText} title="Devis">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center"><div className="text-[22px] font-bold text-navy-900">{d.devis.total}</div><div className="text-[11px] text-navy-400">émis</div></div>
            <div className="text-center"><div className="text-[22px] font-bold text-emerald-600">{pct(d.devis.taux_acceptation)}</div><div className="text-[11px] text-navy-400">acceptation</div></div>
            <div className="text-center"><div className="text-[18px] font-bold text-gold-700">{formatMontant(d.devis.potentiel_en_cours)}</div><div className="text-[11px] text-navy-400">potentiel en cours</div></div>
          </div>
          <Bars data={d.devis.par_statut.map(x => ({ statut: x.statut, n: x.n }))} labelKey="statut" valueKey="n" color="#c0985a" />
        </Section>

        {/* Recrutement & équipe */}
        <Section icon={Users} title="Équipe & recrutement">
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="text-center"><div className="text-[22px] font-bold text-navy-900">{d.equipe.total}</div><div className="text-[11px] text-navy-400">salariées</div></div>
            <div className="text-center"><div className="text-[22px] font-bold text-navy-900">{d.recrutement.total}</div><div className="text-[11px] text-navy-400">candidatures</div></div>
          </div>
          <div className="text-[12px] uppercase tracking-wide text-navy-400 font-semibold mb-2">Candidatures par type</div>
          <Bars data={d.recrutement.par_type.map(x => ({ type: x.type_demande || '—', n: x.n }))} labelKey="type" valueKey="n" color="#7c6cf0" />
        </Section>
      </div>
    </div>
  );
}
